// Single source of truth for card-kind visual specs.
//
// Kind is now purely epistemic — describing the cognitive status of the card
// (statement / question / observation / decision / note). Content-type
// categorization (vision / mechanic / risk / etc) lives at the view layer
// (frame system), not on the card itself.

export interface KindSpec {
  id: string;
  label: string;
  glyph: string;
  color: string;
  bg: string;
  rule: string;
  description: string;
}

export const KIND_SPECS: KindSpec[] = [
  {
    id: 'statement',
    label: 'STATEMENT',
    glyph: '◆',
    color: 'var(--ink-0)',
    bg: 'white',
    rule: 'var(--ink-0)',
    description: 'a self-standing assertion — the main unit of thought',
  },
  {
    id: 'question',
    label: 'QUESTION',
    glyph: '?',
    color: 'var(--amber)',
    bg: 'var(--amber-soft)',
    rule: 'var(--amber)',
    description: 'an open question awaiting an answer',
  },
  {
    id: 'gap',
    label: 'GAP',
    glyph: '◌',
    color: 'var(--red)',
    bg: 'var(--amber-soft)',
    rule: 'var(--red)',
    description: 'a blocking unknown — names which cards it blocks',
  },
  {
    id: 'observation',
    label: 'OBSERVATION',
    glyph: '§',
    color: 'var(--ink-2)',
    bg: 'var(--paper-1)',
    rule: 'var(--rule-s)',
    description: 'an observed fact or quote (with source if external)',
  },
  {
    id: 'decision',
    label: 'DECISION',
    glyph: '▸',
    color: 'var(--ink-0)',
    bg: 'var(--paper-1)',
    rule: 'var(--ink-0)',
    description: 'a firm conclusion committed to',
  },
  {
    id: 'note',
    label: 'NOTE',
    glyph: '·',
    color: 'var(--ink-3)',
    bg: 'white',
    rule: 'var(--rule)',
    description: 'an unstructured stub',
  },
  // doc/11 PR-A1: dead_end is a finished cognitive state ("this path is dead").
  // Visual cue is "muted + struck-through" — present but de-emphasized so cross-
  // thread agent recall sees it WITHOUT it pulling focus in the list view.
  {
    id: 'dead_end',
    label: 'DEAD END',
    glyph: '✕',
    color: 'var(--ink-3)',
    bg: 'var(--paper-0)',
    rule: 'var(--rule)',
    description: 'a tried-and-failed path — kept so future work doesn\'t repeat it',
  },
  // pivot = "we changed direction from X to Y". Amber because it represents a
  // significant state change worth user attention, but not a problem like gap.
  {
    id: 'pivot',
    label: 'PIVOT',
    glyph: '⟿',
    color: 'var(--amber)',
    bg: 'var(--paper-1)',
    rule: 'var(--amber)',
    description: 'a direction change from one idea to another, with the trigger captured',
  },
];

const FALLBACK: KindSpec = {
  id: '_',
  label: 'CARD',
  glyph: '·',
  color: 'var(--ink-2)',
  bg: 'white',
  rule: 'var(--rule)',
  description: 'unknown',
};

export function specForKind(kind: string): KindSpec {
  const found = KIND_SPECS.find((s) => s.id === kind);
  if (found) return found;
  return { ...FALLBACK, label: kind.toUpperCase() };
}

export const KIND_ORDER: string[] = KIND_SPECS.map((s) => s.id);
